import React, { useState } from 'react';
import { Popover, Tooltip, message } from 'antd';
import { FolderOpenOutlined, DownloadOutlined, LoadingOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import styled from 'styled-components';
import { getI18n, useTranslation } from 'react-i18next';
import TableHeader from '~/pages/agent/components/agent/tableElements/TableHeader';
import { AgentApplication, Way4ExtractFile } from '~/types/AgentApplication';
import { downloadWay4File } from '~/api/agent';

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const Way4FilesFolder = ({ files }: { files: Way4ExtractFile[] }) => {
  const { t, i18n } = useTranslation('agent');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownload = async (file: Way4ExtractFile, event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (downloadingId) return;
    setDownloadingId(file.id);
    try {
      const response = await downloadWay4File(file.id);
      downloadBlob(response.data, file.name || `${file.id}.csv`);
    } catch (err: any) {
      const apiError = err?.response?.data;
      const isArabic = i18n.language === 'ar';
      const errorMsg =
        (isArabic ? apiError?.arDescription : apiError?.description) ??
        t('Way4 download error');
      void message.error(errorMsg);
    } finally {
      setDownloadingId(null);
    }
  };

  const content = (
    <FileList onClick={(e) => e.stopPropagation()}>
      {files.map((file) => (
        <FileRow
          key={file.id}
          onClick={(e) => {
            void handleDownload(file, e);
          }}
          role="button"
          tabIndex={0}
        >
          <span className="way4FileName">{file.name || file.product || file.id}</span>
          {downloadingId === file.id ? <LoadingOutlined /> : <DownloadOutlined />}
        </FileRow>
      ))}
    </FileList>
  );

  return (
    <Popover
      content={content}
      title={t('Way4 onboarding files')}
      trigger="click"
      placement="bottomRight"
    >
      <FolderButton
        onClick={(e) => e.stopPropagation()}
        aria-label={t('Way4 onboarding files')}
      >
        <Tooltip title={t('Way4 Files tooltip', { count: files.length })}>
          <span className="way4FolderIcon">
            <FolderOpenOutlined />
            <span className="way4FileCount">{files.length}</span>
          </span>
        </Tooltip>
      </FolderButton>
    </Popover>
  );
};

function way4FilesCell(): ColumnsType<AgentApplication>[0] {
  return {
    title: () => <TableHeader title={getI18n().t('Way4 Files', { ns: 'agent' })} />,
    dataIndex: 'way4ExtractFiles',
    key: 'way4ExtractFiles',
    render: (files?: Way4ExtractFile[]) =>
      files && files.length > 0 ? <Way4FilesFolder files={files} /> : <Empty>-</Empty>,
    width: 70,
  };
}

const FolderButton = styled.div`
  display: inline-flex;
  align-items: center;
  cursor: pointer;

  .way4FolderIcon {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 20px;
  }

  .way4FileCount {
    font-size: 12px;
    font-weight: 600;
  }
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 220px;
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 8px;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  .way4FileName {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 260px;
  }
`;

const Empty = styled.span`
  color: rgba(0, 0, 0, 0.35);
`;

export default way4FilesCell;
